import { ClientFiles } from "@/components/ClientFiles";
import { ClientRetainers } from "@/components/ClientRetainers";
import { ClientPaymentPlans } from "@/components/PaymentPlan";
import { Paperclip } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, Card } from "@/components/ui-helpers";
import { StatusBadge } from "@/components/StatusBadge";
import { COUNTRIES } from "@/lib/countries";
import { TeamLinkedMessages } from "@/components/TeamLinkedMessages";
import { ClientNoteLog } from "@/components/ClientNoteLog";
import { ClientChangeHistory } from "@/components/ClientChangeHistory";
import { ClientInteractions } from "@/components/ClientInteractions";
import { DuplicateGuard } from "@/components/DuplicateGuard";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { CaseOwnership, ImportantTag } from "@/components/CaseOwnership";
import { useStaffNames } from "@/hooks/useStaffNames";
import { AppointmentsPanel } from "@/components/AppointmentsPanel";
import { DeleteClientButton } from "@/components/ClientDelete";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, Save, UserCog, Ban, CheckCircle2, Lock, FolderOpen, Plus, ChevronRight, User, Archive, RotateCcw, LayoutDashboard, MessageSquareText, Wallet, Users } from "lucide-react";
import { ClientMergeDialog } from "@/components/ClientMergeDialog";

const PERSONAL = [
  ["full_name", "Full Name", "text"], ["email", "Email", "text"], ["phone", "Phone", "text"],
  ["dob", "Date of Birth", "date"], ["gender", "Gender", "text"], ["address", "Address", "text"],
  ["citizenship", "Country of Citizenship", "text"], ["residence", "Country of Residence", "text"],
];
const IMMIGRATION = [
  ["immigration_status", "Immigration Status", "text"], ["status_expiry", "Status Expiry", "date"],
  ["uci", "UCI", "text"], ["passport_no", "Passport Number", "text"], ["passport_expiry", "Passport Expiry", "date"],
];
const INTERNAL = [["lead_source", "Lead Source", "text"], ["client_type", "Client Type", "text"]];
const TAB_KEYS = ["overview", "details", "cases", "activity", "billing", "files"];

export default function AdminClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const canManage = user?.role === "admin" || (user?.permissions || []).includes("edit_clients");
  const canArchive = user?.role === "admin" || (user?.permissions || []).includes("edit_clients");
  const [client, setClient] = useState(null);
  const [form, setForm] = useState({});
  const [staff, setStaff] = useState([]);
  const [saving, setSaving] = useState(false);
  const [params, setParams] = useSearchParams();
  const tab = TAB_KEYS.includes(params.get("tab")) ? params.get("tab") : params.get("tab") === "profile" ? "details" : "overview";
  const setTab = (t) => setParams(t === "overview" ? {} : { tab: t }, { replace: true });
  const [cases, setCases] = useState(null);
  const [fees, setFees] = useState(null);
  const [usedCountries, setUsedCountries] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const canCreateCase = user?.role === "admin" || (user?.permissions || []).includes("edit_cases");
  const canEditClient = user?.role === "admin" || (user?.permissions || []).includes("edit_clients");
  const [mergeOpen, setMergeOpen] = useState(false);

  const load = () => api.get(`/clients/${id}`).then((r) => { setClient(r.data); setForm(r.data); }).catch(() => setClient(false));
  const loadFees = () => api.get(`/clients/${id}/fee-summary`).then((r) => setFees(r.data)).catch(() => {});
  useEffect(() => {
    load(); api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
    api.get("/applications", { params: { client_id: id } }).then((r) => setCases(r.data)).catch(() => setCases([]));
    api.get(`/clients/${id}/fee-summary`).then((r) => setFees(r.data)).catch(() => setFees(false));
    api.get("/settings").then((r) => setStatuses(r.data.client_statuses || [])).catch(() => {});
    // countries already used on other client files, most frequent first
    api.get("/clients").then((r) => {
      const n = {};
      r.data.forEach((c) => [c.citizenship, c.residence].forEach((v) => { const t = (v || "").trim(); if (t) n[t] = (n[t] || 0) + 1; }));
      setUsedCountries(Object.keys(n).sort((a, b) => n[b] - n[a]));
    }).catch(() => {});
  }, [id]);

  if (client === null) return <Loader />;
  if (client === false) return <Card className="p-6">Client not found.</Card>;

  const save = async () => {
    setSaving(true);
    try {
      const keys = [...PERSONAL, ...IMMIGRATION, ...INTERNAL].map((x) => x[0]).concat(["assigned_staff", "assigned_consultant", "notes"]);
      const payload = {}; keys.forEach((k) => { payload[k] = form[k] ?? ""; }); payload.promo_consent = !!form.promo_consent;
      const { data } = await api.put(`/clients/${id}`, payload);
      setClient(data); setForm(data); toast.success("Client saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const toggleActive = async () => {
    try { const { data } = await api.patch(`/clients/${id}/disable`); setClient({ ...client, active: data.active }); toast.success(data.active ? "Client enabled" : "Client disabled"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const toggleArchive = async () => {
    const goingToArchive = !client.archived;
    if (goingToArchive && !window.confirm(`Mark ${client.full_name} inactive? They move to the Inactive tab, still show in search, and can be reactivated anytime.`)) return;
    try {
      const { data } = await api.patch(`/clients/${id}/archive`, { archived: goingToArchive });
      setClient(data);
      toast.success(goingToArchive ? "Client marked inactive" : "Client reactivated");
      if (goingToArchive) nav("/admin/clients");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const patchField = async (patch) => {
    try {
      const { data } = await api.put(`/clients/${id}`, patch);
      setClient(data); setForm((f) => ({ ...f, ...patch }));
      toast.success("Saved");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const inputCls = "mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm disabled:bg-slate-50 focus:ring-2 focus:ring-primary/30";
  const Field = ([k, label, type]) => (
    <div key={k}>
      <label className="text-xs font-medium text-slate-500">{label}</label>
      {k === "gender" ? (
        <select disabled={!canManage} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`client-field-${k}`} className={inputCls}>
          <option value="">Select…</option>
          {["Male", "Female"].map((g) => <option key={g} value={g}>{g}</option>)}
          {form[k] && !["Male", "Female"].includes(form[k]) && <option value={form[k]}>{form[k]}</option>}
        </select>
      ) : (k === "citizenship" || k === "residence") ? (
        <input list="country-options" disabled={!canManage} value={form[k] || ""} placeholder="Search country…" onChange={(e) => setForm({ ...form, [k]: e.target.value })}
          data-testid={`client-field-${k}`} className={inputCls} autoComplete="off" />
      ) : (
        <input type={type} disabled={!canManage} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
          data-testid={`client-field-${k}`} className={inputCls} />
      )}
    </div>
  );
  const countryOptions = [...usedCountries, ...COUNTRIES.filter((c) => !usedCountries.includes(c))];

  const staffName = (sid) => staff.find((x) => x.id === sid)?.name || "Unassigned";
  const tabs = [["overview", "Overview", LayoutDashboard], ["details", "Details", User], ["cases", `Cases${cases ? ` (${cases.length})` : ""}`, FolderOpen],
    ["activity", "Notes & Activity", MessageSquareText], ["billing", "Billing", Wallet], ["files", "Files", Paperclip]];

  return (
    <div className="space-y-6">
      <Link to="/admin/clients" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-primary"><ChevronLeft className="h-4 w-4" /> Back to clients</Link>

      <Card className="p-5" data-testid="client-header">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <div className="h-14 w-14 rounded-2xl bg-secondary text-white flex items-center justify-center font-head font-extrabold text-2xl shrink-0">{client.full_name?.[0]}</div>
            <div className="min-w-0">
              <h1 className="font-head font-extrabold text-2xl text-slate-900 truncate">{client.full_name}</h1>
              <div className="flex items-center gap-2 min-w-0">
                <div className="text-slate-500 text-sm truncate">{[client.email, client.phone].filter(Boolean).join(" · ") || "No contact details"}</div>
                <WhatsAppButton variant="pill" testid="client-whatsapp-btn" target={{ kind: "client", id, name: client.full_name, phone: client.phone, file_number: client.file_number }} />
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <StatusBadge status={client.status || "New"} />
                {!client.active && <StatusBadge status="Disabled" />}
                {client.archived && <span data-testid="client-archived-badge" className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full"><Archive className="h-3 w-3" /> Inactive</span>}
                <span className="font-mono text-xs text-slate-500" data-testid="client-file-number-display">File #{client.file_number || "—"}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            {canEditClient && (<>
              <div className="w-[calc(50%-4px)] sm:w-40">
                <label className="text-[11px] font-medium text-slate-500">Status</label>
                <select data-testid="client-status-select" value={form.status || "New"} onChange={(e) => patchField({ status: e.target.value })} className={inputCls}>
                  {(statuses.length ? statuses : [form.status || "New"]).map((st) => <option key={st} value={st}>{st}</option>)}
                  {form.status && statuses.length > 0 && !statuses.includes(form.status) && <option value={form.status}>{form.status}</option>}
                </select>
              </div>
              <div className="w-[calc(50%-4px)] sm:w-40">
                <label className="text-[11px] font-medium text-slate-500">File number</label>
                <input data-testid="client-file-number-input" value={form.file_number || ""} onChange={(e) => setForm({ ...form, file_number: e.target.value })}
                  onBlur={() => { const v = (form.file_number || "").trim(); if (v !== (client.file_number || "")) patchField({ file_number: v }); }}
                  onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                  className={inputCls} placeholder="e.g. CHAHAL-1995" />
              </div>
            </>)}
            {canManage && (
              <button onClick={toggleActive} data-testid="toggle-active" className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm ${client.active ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"}`}>
                {client.active ? <><Ban className="h-4 w-4" /> Disable</> : <><CheckCircle2 className="h-4 w-4" /> Enable</>}
              </button>
            )}
            {canEditClient && (
              <button onClick={() => setMergeOpen(true)} data-testid="client-merge-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm bg-slate-100 text-slate-600 hover:bg-slate-200"><Users className="h-4 w-4" /> Merge duplicates</button>
            )}
            {user?.role === "admin" && <DeleteClientButton client={client} onDeleted={() => nav("/admin/clients")} />}
            {canArchive && (
              <button onClick={toggleArchive} data-testid="toggle-archive" className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm ${client.archived ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-600"}`}>
                {client.archived ? <><RotateCcw className="h-4 w-4" /> Reactivate</> : <><Archive className="h-4 w-4" /> Mark inactive</>}
              </button>
            )}
          </div>
        </div>
      </Card>

      <datalist id="country-options">
        {countryOptions.map((c, i) => <option key={c} value={c}>{i < usedCountries.length ? "Previously used" : ""}</option>)}
      </datalist>
      {mergeOpen && <ClientMergeDialog clientId={id} onClose={() => setMergeOpen(false)} onMerged={(keptId) => { setMergeOpen(false); if (keptId === id) window.location.reload(); else nav(`/admin/clients/${keptId}`); }} />}

      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto no-scrollbar" data-testid="client-tabs">
        {tabs.map(([k, label, Icon]) => (
          <button key={k} data-testid={`client-tab-${k}`} onClick={() => setTab(k)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === k ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid lg:grid-cols-3 gap-6" data-testid="client-overview">
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-6" data-testid="client-summary">
              <h3 className="font-head font-bold text-slate-900 mb-4">Summary</h3>
              <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {[["Assigned to", staffName(client.assigned_staff) || staffName(client.assigned_consultant)],
                  ["Immigration status", [client.immigration_status, client.status_expiry && `expires ${client.status_expiry}`].filter(Boolean).join(" · ")],
                  ["Passport expiry", client.passport_expiry], ["UCI", client.uci], ["Lead source", client.lead_source], ["Client type", client.client_type]].map(([l, v]) => (
                  <div key={l}><dt className="text-xs text-slate-500">{l}</dt><dd className="font-medium text-slate-800">{v || "—"}</dd></div>
                ))}
              </dl>
            </Card>
            {fees && <BalanceStrip fees={fees} onOpen={() => setTab("billing")} />}
            <ClientRetainers clientId={id} />
            <Card className="p-6"><AppointmentsPanel clientId={id} /></Card>
          </div>
          <div className="space-y-6">
            <Card className="p-6"><ClientNoteLog clientId={id} canManage={canManage} /></Card>
            <Card className="p-6"><ClientChangeHistory clientId={id} /></Card>
          </div>
        </div>
      )}

      {tab === "details" && (<>
        <div className="grid lg:grid-cols-3 gap-6" data-testid="client-details">
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-6">
              <h3 className="font-head font-bold text-slate-900 mb-4">Personal Information</h3>
              <div className="grid sm:grid-cols-2 gap-4">{PERSONAL.map(Field)}</div>
              <div className="mt-4"><DuplicateGuard email={form.email} phone={form.phone} excludeId={id} enabled={canManage} testId="client-duplicate-warning" /></div>
            </Card>
            <Card className="p-6">
              <h3 className="font-head font-bold text-slate-900 mb-4">Immigration Information</h3>
              <div className="grid sm:grid-cols-2 gap-4">{IMMIGRATION.map(Field)}</div>
            </Card>
          </div>
          <div className="space-y-6">
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4"><UserCog className="h-4 w-4 text-primary" /><h3 className="font-head font-bold text-slate-900">Internal (Staff Only)</h3></div>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-slate-500">Assigned to</label>
                  <select disabled={!canManage} value={form.assigned_staff || form.assigned_consultant || ""} onChange={(e) => setForm({ ...form, assigned_staff: e.target.value, assigned_consultant: e.target.value })} data-testid="assign-to" className={inputCls}>
                    <option value="">Unassigned</option>
                    {staff.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                  </select>
                </div>
                {INTERNAL.map(Field)}
                <label className="flex items-start gap-2.5 rounded-xl border border-slate-100 p-3 cursor-pointer" data-testid="client-promo-consent">
                  <input type="checkbox" disabled={!canManage} checked={!!form.promo_consent} onChange={(e) => setForm({ ...form, promo_consent: e.target.checked })} className="h-4 w-4 mt-0.5 accent-primary" />
                  <span><span className="text-sm font-semibold text-slate-800">OK to send promotions</span><span className="block text-[11px] text-slate-400">Tick only once the client agrees to receive promotional WhatsApp messages.</span></span>
                </label>
              </div>
            </Card>
            {!canManage && <div className="flex items-center gap-2 text-xs text-slate-400"><Lock className="h-3.5 w-3.5" /> Read-only — you don't have edit permission.</div>}
          </div>
        </div>
        {canManage && (
          <div className="sticky bottom-20 md:bottom-6 flex justify-end">
            <button onClick={save} disabled={saving} data-testid="save-client" className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-xl font-semibold shadow-xl shadow-primary/25 disabled:opacity-60">
              <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        )}
      </>)}

      {tab === "cases" && <ClientCases clientId={id} cases={cases} canCreate={canCreateCase} fees={fees} />}
      {tab === "cases" && <TeamLinkedMessages clientId={id} />}

      {tab === "activity" && (
        <div className="grid lg:grid-cols-2 gap-6" data-testid="client-activity">
          <div className="space-y-6">
            <ClientInteractions clientId={id} canLog={canEditClient} />
            <TeamLinkedMessages clientId={id} />
          </div>
          <div className="space-y-6">
            <Card className="p-6"><ClientNoteLog clientId={id} canManage={canManage} /></Card>
            <Card className="p-6"><ClientChangeHistory clientId={id} /></Card>
          </div>
        </div>
      )}

      {tab === "files" && <ClientFiles clientId={id} />}
      {tab === "billing" && (
        <div className="space-y-6" data-testid="client-billing">
          {fees === null ? <Loader /> : fees === false ? <Card className="p-6 text-sm text-slate-500">Billing details aren't available for your account.</Card> : (<>
            <BalanceOverview fees={fees} />
            <ClientPaymentPlans clientId={id} />
            {canCreateCase && <AddCaseFee cases={(cases || []).filter((a) => !isClosed(a))} cur={fees.currency} onDone={loadFees} />}
            <CaseGroup title="Balance by case" testid="client-billing-cases" items={cases || []} empty="No cases yet." feeById={Object.fromEntries((fees.cases || []).map((c) => [c.id, c]))} cur={fees.currency} />
          </>)}
        </div>
      )}
    </div>
  );
}

const CLOSED_STATUSES = ["Completed", "Cancelled", "Closed", "Refused", "Withdrawn"];
const isClosed = (a) => CLOSED_STATUSES.includes(a.status) || ["Closed", "Completed"].includes(a.current_stage);

function ClientCases({ clientId, cases, canCreate, fees }) {
  const feeById = Object.fromEntries(((fees && fees.cases) || []).map((c) => [c.id, c]));
  if (cases === null) return <Loader />;
  const active = cases.filter((a) => !isClosed(a));
  const old = cases.filter(isClosed);
  return (
    <div className="space-y-6" data-testid="client-cases">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-500">All immigration files for this client. Click a case to open it.</p>
        {canCreate && (
          <Link to={`/admin/applications?new=1&client=${clientId}`} data-testid="client-new-case" className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2.5 rounded-xl font-semibold text-sm"><Plus className="h-4 w-4" /> New case</Link>
        )}
      </div>
      <CaseGroup title="Active cases" testid="client-cases-active" items={active} empty="No active cases." feeById={feeById} cur={fees?.currency} />
      <CaseGroup title="Old / closed cases" testid="client-cases-closed" items={old} empty="No closed cases." muted feeById={feeById} cur={fees?.currency} />
    </div>
  );
}

function CaseGroup({ title, items, empty, muted, testid, feeById = {}, cur = "CAD" }) {
  const names = useStaffNames();
  return (
    <Card className="p-0 overflow-hidden" data-testid={testid}>
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-head font-bold text-slate-900">{title}</h3>
        <span className="text-xs text-slate-400">{items.length}</span>
      </div>
      {items.length === 0 ? <div className="px-5 py-6 text-sm text-slate-400">{empty}</div> : (
        <div className="divide-y divide-slate-100">
          {items.map((a) => (
            <Link key={a.id} to={`/admin/applications/${a.id}`} data-testid={`client-case-${a.id}`}
              className={`flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50 transition-colors ${a.important ? "border-l-4 border-l-rose-500" : ""} ${muted ? "opacity-80" : ""}`}>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs text-slate-500">{a.app_number}</span>
                  <span className="font-semibold text-slate-900 truncate">{a.case_name || a.service_type}</span>
                  {a.important && <ImportantTag />}
                </div>
                <CaseOwnership app={a} names={names} showClient={false} className="mt-0.5" />
                <div className="text-xs text-slate-500 mt-0.5">Stage: {a.current_stage || "—"} · Opened {(a.date_opened || a.created_at || "").slice(0, 10)}{a.ircc_number ? ` · IRCC ${a.ircc_number}` : ""}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {feeById[a.id] && (feeById[a.id].configured
                  ? <span className="text-xs text-right mr-2" data-testid={`client-case-balance-${a.id}`}><span className="text-slate-400">Balance</span> <b className={feeById[a.id].balance > 0 ? "text-rose-600" : "text-emerald-600"}>{money(feeById[a.id].balance, cur)}</b></span>
                  : <span className="text-[11px] text-amber-600 mr-2">Fee not configured</span>)}
                <StatusBadge status={a.status} />
                {a.priority && a.priority !== "Normal" && <StatusBadge status={a.priority} />}
                <ChevronRight className="h-4 w-4 text-slate-300" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
}

const money = (v, cur = "CAD") => `${cur} $${Number(v || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function BalanceOverview({ fees }) {
  const cur = fees.currency;
  const items = [["Total fees (incl. HST)", fees.total_fees, "text-slate-900", "client-total-fees"], ["Payments received", fees.total_paid, "text-emerald-600", "client-total-paid"],
                 ["Remaining balance", fees.balance, fees.balance > 0 ? "text-rose-600" : "text-emerald-600", "client-total-balance"]];
  return (
    <Card className="p-5" data-testid="client-balance-overview">
      <div className="grid sm:grid-cols-3 gap-4">
        {items.map(([label, v, color, tid]) => (
          <div key={label}>
            <div className="text-xs text-slate-500">{label}</div>
            <div data-testid={tid} className={`font-head font-extrabold text-xl ${color}`}>{money(v, cur)}</div>
          </div>
        ))}
      </div>
      <div className="text-[11px] text-slate-400 mt-2">
        Across {fees.cases.length} case{fees.cases.length === 1 ? "" : "s"}{fees.other_invoices.count ? ` + ${fees.other_invoices.count} other invoice${fees.other_invoices.count === 1 ? "" : "s"}` : ""}.
        {fees.unconfigured_cases > 0 && <span className="text-amber-600"> {fees.unconfigured_cases} case{fees.unconfigured_cases === 1 ? " has" : "s have"} no fee configured and {fees.unconfigured_cases === 1 ? "is" : "are"} not included.</span>}
      </div>
    </Card>
  );
}

function BalanceStrip({ fees, onOpen }) {
  const cur = fees.currency;
  return (
    <button type="button" onClick={onOpen} data-testid="client-balance-strip" className="w-full text-left">
      <Card className="p-4 grid grid-cols-3 gap-3 hover:shadow-md transition-shadow">
        {[["Total fees", fees.total_fees, "text-slate-900"], ["Paid", fees.total_paid, "text-emerald-600"], ["Remaining", fees.balance, fees.balance > 0 ? "text-rose-600" : "text-emerald-600"]].map(([l, v, c]) => (
          <div key={l}><div className="text-[11px] text-slate-500">{l}</div><div className={`font-head font-bold text-base ${c}`}>{money(v, cur)}</div></div>
        ))}
      </Card>
    </button>
  );
}


// Adds an additional fee line to one of the client's cases, reusing the case's own
// per-case fee editor (PUT /applications/:id/fee-items with the full extra_fees list).
function AddCaseFee({ cases, cur, onDone }) {
  const [open, setOpen] = useState(false);
  const [appId, setAppId] = useState("");
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => { setAppId(""); setLabel(""); setAmount(""); };
  const submit = async () => {
    const amt = parseFloat(amount);
    if (!appId) return toast.error("Pick a case");
    if (!label.trim()) return toast.error("Enter a description for the fee");
    if (!(amt > 0)) return toast.error("Enter an amount greater than 0");
    setSaving(true);
    try {
      const { data: current } = await api.get(`/applications/${appId}/fees`);
      const existing = (current.extra_fees || []).map((x) => ({ label: x.label, amount: x.amount }));
      await api.put(`/applications/${appId}/fee-items`, { extra_fees: [...existing, { label: label.trim(), amount: amt }] });
      toast.success("Case fee added");
      setOpen(false); reset(); onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  if (!cases || cases.length === 0) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <button data-testid="client-add-case-fee" className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2.5 rounded-xl font-semibold text-sm">
          <Plus className="h-4 w-4" /> Add case fee
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Add a fee to a case</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-500">Case</label>
            <select data-testid="add-fee-case" value={appId} onChange={(e) => setAppId(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-primary/30">
              <option value="">Select a case…</option>
              {cases.map((a) => <option key={a.id} value={a.id}>{a.case_name || a.service_type || "Case"}{a.app_number ? ` · ${a.app_number}` : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Description</label>
            <input data-testid="add-fee-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Additional document review" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-primary/30" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Amount ({cur || "CAD"})</label>
            <input data-testid="add-fee-amount" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-primary/30" />
          </div>
          <p className="text-[11px] text-slate-400">This adds an additional-fee line to the selected case, exactly like the per-case fee editor. HST applies per your settings.</p>
        </div>
        <DialogFooter>
          <button data-testid="add-fee-submit" onClick={submit} disabled={saving} className="bg-primary text-white px-5 py-2.5 rounded-xl font-semibold text-sm disabled:opacity-60">{saving ? "Adding…" : "Add fee"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
